import type { i18n as I18nInstance } from 'i18next';
import type { QuestionConfigQuestionType } from '/index';

import { basketTableTotalTypes, questionScopeCategories, questionSelectorOptions } from '/business/shared/util/Statics';
import { i18n } from '/shared/translations/i18n';
import formatNumber from '/shared/functions/formatNumber';
import getCurrencySymbol from '/shared/functions/getCurrencySymbol';

import transferIcon from '/business/shared/images/icons/bp-goods-transfer-pictograms-bp-green-.svg';
import initiativeCardBackground from '/business/shared/images/tip-img-nov2024/Capital Goods card image.jpg';
import initiativeBackground from '/business/shared/images/tip-img-nov2024/Capital Goods card image@2x.jpg';

import educationContentJson from '/business/shared/components/questions/scope3/goods/purchasedGoods/_education/purchasedGoods.json';
import useGetPurchasedGoodsSpendDataSchema from '/business/shared/components/questions/scope3/goods/purchasedGoods/_hooks/useGetPurchasedGoodsSpendDataSchema';
import useFormatSharedScope3GoodsSupplier from '/business/shared/components/pages/question/formats/supplierDataFormats/useFormatSharedScope3GoodsSupplier';
import useFormatSharedScope3GoodsCustom from '/business/shared/components/pages/question/formats/customDataFormats/useFormatSharedScope3GoodsCustom';

import { bottomContentsSupplier } from '/business/shared/components/questions/scope3/_education/bottomScope3';
import ReportingPeriod from '/business/shared/components/widgets/reporting-period/ReportingPeriod';
import StyledParagraph from '/shared/styled/components/StyledParagraph';
import StyledInfoFlash from '/shared/styled/StyledInfoFlash';
import {
  bottomPurchasedCapitalActivity,
  bottomPurchasedCapitalAll,
} from '/business/shared/components/questions/scope3/_education/bottomPurchasedCapital';

export const getConfigurePurchasedGoodsAndServices = (
  _options: { i18n?: I18nInstance } = {},
): QuestionConfigQuestionType => {
  return {
    title: i18n.t('business:QuestionPage.forms.emissionSelector.purchasedGoodsAndServices'),
    id: 'purchased-goods-and-services',
    apiKey: 'calculation-purchased-goods-spend-data',
    initiative: {
      getVisuals: () => {
        return {
          description: false,
          background: initiativeBackground,
          useMask: false,
          cardBackground: initiativeCardBackground,
        };
      },
    },
    table: {
      title: 'Calculated carbon emissions for spend data: ',
      totalType: basketTableTotalTypes.TONNAGE,
      headings: [
        i18n.t('business:Questions.calculations.purchasedGoodsAndServices.table.heading1'),
        i18n.t('business:Questions.calculations.purchasedGoodsAndServices.table.heading2'),
        i18n.t('business:Questions.calculations.purchasedGoodsAndServices.table.heading3'),
      ].filter((item) => !!item),
      cells: [
        'spendCategoryLabel',
        ({
          spendValue,
          applicationCurrency,
          userLang,
        }: {
          spendValue: number;
          applicationCurrency: string;
          userLang: string;
        }) => {
          const currencyStr = getCurrencySymbol({ currencyCode: applicationCurrency, userLang });
          return `${currencyStr}${formatNumber(spendValue)}`;
        },
        ({ tonnage }: { tonnage: number }) => `${formatNumber(tonnage)} tCO\u2082e`,
      ].filter((item) => !!item),
      isAnswerEditable: true,
      tabName: 'Activity data',
      mainQuestionTitle: 'Calculated carbon emissions for purchased goods and services: ',
      supplier: {
        columns: [
          'emissionSource',
          'supplierName',
          'supplierIntensityDataOrAbsoluteEmissions',
          'totalSpend',
          'carbonEmissions',
        ],
      },
      custom: {
        columns: ['emissionSource', 'intensityValue', 'unitOfMeasurement', 'quantity', 'carbonEmissions'],
      },
    },

    primaryI18nKey: 'business:QuestionPage.forms.emissionSelector.purchasedGoodsAndServices',
    subTextI18nKey: 'business:QuestionPage.forms.emissionSelector.purchasedGoodsAndServicesTip',
    overviewPageTitleI18nKey: 'business:QuestionPage.forms.emissionSelector.purchasedGoodsAndServices',
    useHookLoader: useGetPurchasedGoodsSpendDataSchema,
    scopeCategory: questionScopeCategories.SCOPE_3,
    icon: transferIcon,
    isCompletePropName: 'isPurchasedGoodsComplete',
    selector: {
      isActivityBranchNamedSpendData: true,
      injectQuestionBranchSelector: true,
      questionBranchEnableCustomData: true,
      questionBranchEnableSupplierData: true,
      para: 'To calculate emissions associated with the products and services your business purchases, you can use a combination of spend data, supplier data, and custom data.',
      options: [
        {
          title: questionSelectorOptions.ACTIVITY,
          requiredData: 'Spend category, total spend.',
        },
        {
          title: questionSelectorOptions.SUPPLIER,
          requiredData:
            'Spend category, supplier name, product description, supplier intensity data/absolute emissions, no. of units purchased, total spend.',
        },
        {
          title: questionSelectorOptions.CUSTOM,
          requiredData:
            'Emission source, product description, intensity data/absolute emissions, quantity of units purchased, total spend.',
        },
      ],
    },
    education: {
      hasEducationWidget: true,
      educationContent: educationContentJson,
      bottomContentsSupplier: bottomContentsSupplier,
      bottomContentsAll: bottomPurchasedCapitalAll,
      bottomContentsActivity: bottomPurchasedCapitalActivity,
    },
    useQuestionBranchSupplierLoader: () =>
      useFormatSharedScope3GoodsSupplier({
        questionFormatName: 'purchased-goods-and-services',
      }),
    useQuestionBranchCustomLoader: () =>
      useFormatSharedScope3GoodsCustom({ questionFormatName: 'purchased-goods-and-services' }),
    supplierAndCustomSubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          Enter data provided by suppliers for purchases during the reporting period: <ReportingPeriod $green />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
    activitySubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          Enter the amount spent in each of the following categories during the reporting period:{' '}
          <ReportingPeriod $green />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
  };
};
